import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const IPhoneAkkuTauschenPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'iPhone Akku tauschen – Alle Modelle & Preise | PhoneMedic',
      description: 'iPhone Akku tauschen ✓ Alle Modelle iPhone 16 bis SE ✓ ab 49€ ✓ Original Qualität ✓ 12 Monate Garantie ✓ Express 24h ✓ Bundesweiter Einsende-Service | PhoneMedic',
      keywords: ['iPhone Akku tauschen', 'iPhone Akku wechseln', 'iPhone Akku kaputt', 'iPhone Akku Kosten', 'iPhone Batterie tauschen', 'iPhone Akku schnell leer'],
      canonicalUrl: 'https://www.phone-medic.de/iphone-akku-tauschen',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'iPhone Akku tauschen',
        description: 'Professioneller iPhone Akkutausch für alle Modelle. Original Qualität, 12 Monate Garantie, Express 24h Service.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: [
          { '@type': 'Offer', name: 'iPhone SE Akku', price: '49.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'iPhone 13 Akku', price: '69.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'iPhone 15 Akku', price: '89.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        ],
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Wann sollte ich den iPhone Akku tauschen?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der Akku sollte getauscht werden wenn die Akkugesundheit unter 80% liegt (Einstellungen → Batterie → Batteriezustand), das iPhone unter Last plötzlich ausgeht, sich ungewöhnlich stark erhitzt oder weniger als 3–4 Stunden Bildschirmzeit bei normaler Nutzung hält.' },
          },
          {
            '@type': 'Question',
            name: 'Was kostet ein iPhone Akkutausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die Preise variieren je nach Modell: iPhone SE ab 49€, iPhone 11 ab 59€, iPhone 12/13 ab 69€, iPhone 14 ab 79€, iPhone 15 ab 89€, iPhone 16 ab 99€. Alle Preise inklusive Einbau und 12 Monate Garantie.' },
          },
          {
            '@type': 'Question',
            name: 'Verliert mein iPhone Daten beim Akkutausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nein. Beim Akkutausch wird ausschließlich der Akku ausgetauscht. Alle Daten, Apps und Einstellungen bleiben vollständig erhalten.' },
          },
          {
            '@type': 'Question',
            name: 'Wie lange dauert ein iPhone Akkutausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der Akkutausch dauert 30–60 Minuten. Inklusive Versandzeit rechne mit 3–5 Werktagen. Express-Service innerhalb von 24h auf Anfrage möglich.' },
          },
          {
            '@type': 'Question',
            name: 'Mein iPhone zeigt nach dem Akkutausch eine Warnung – ist das normal?',
            acceptedAnswer: { '@type': 'Answer', text: 'Bei neueren iPhone-Modellen (ab iPhone 11) zeigt iOS eine Meldung wenn ein Akku eingebaut wurde, der nicht direkt von Apple stammt. Diese Meldung ist rein informativ und beeinträchtigt die Funktion nicht. Der Akku arbeitet einwandfrei.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#iphone-akku-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'iphone-akku-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#iphone-akku-schema')?.remove() }
  }, [])

  const prices = [
    { model: 'iPhone SE (2. & 3. Generation)', price: 'ab 49 €' },
    { model: 'iPhone 11', price: 'ab 59 €' },
    { model: 'iPhone 12 / 12 mini', price: 'ab 69 €' },
    { model: 'iPhone 12 Pro / 12 Pro Max', price: 'ab 69 €' },
    { model: 'iPhone 13 / 13 mini', price: 'ab 69 €' },
    { model: 'iPhone 13 Pro / 13 Pro Max', price: 'ab 79 €' },
    { model: 'iPhone 14 / 14 Plus', price: 'ab 79 €' },
    { model: 'iPhone 14 Pro / 14 Pro Max', price: 'ab 89 €' },
    { model: 'iPhone 15 / 15 Plus', price: 'ab 89 €' },
    { model: 'iPhone 15 Pro / 15 Pro Max', price: 'ab 99 €' },
    { model: 'iPhone 16 / 16 Plus', price: 'ab 99 €' },
    { model: 'iPhone 16 Pro / 16 Pro Max', price: 'ab 109 €' },
  ]

  const symptoms = [
    { title: 'Kurze Akkulaufzeit', desc: 'Das iPhone hält keine 4 Stunden mehr durch. Ein deutliches Zeichen für nachlassende Akkukapazität – besonders wenn es früher viel länger hielt.' },
    { title: 'Akkugesundheit unter 80 %', desc: 'Unter Einstellungen → Batterie → Batteriezustand & Laden erkennst du den aktuellen Wert. Apple empfiehlt ab 80 % den Tausch.' },
    { title: 'iPhone geht plötzlich aus', desc: 'Bei 20–30 % Ladeanzeige schaltet sich das iPhone aus? Der Akku kann nicht mehr die nötige Spannung liefern.' },
    { title: 'Starke Erhitzung', desc: 'Ein stark geschwächter Akku muss mehr arbeiten und erzeugt dabei Wärme. Ungewöhnliche Hitze beim Laden oder normaler Nutzung.' },
    { title: 'Drosseln durch iOS', desc: 'iOS drosselt die CPU-Leistung automatisch bei schlechtem Akku – spürbar als Verlangsamung beim Öffnen von Apps oder beim Scrollen.' },
    { title: 'Aufgeblähter Akku', desc: 'Wölbt sich das Display nach oben oder lässt sich das iPhone nicht mehr flach auf den Tisch legen? Sofort tauschen – Brandgefahr.' },
  ]

  const faqs = [
    { q: 'Wann sollte ich den iPhone Akku tauschen?', a: 'Wenn die Akkugesundheit unter 80% liegt, das iPhone unter Last plötzlich ausgeht, sich stark erhitzt oder weniger als 3–4 Stunden Bildschirmzeit bei normaler Nutzung hält.' },
    { q: 'Was kostet ein iPhone Akkutausch?', a: 'iPhone SE ab 49€, iPhone 11 ab 59€, iPhone 12/13 ab 69€, iPhone 14 ab 79€, iPhone 15 ab 89€, iPhone 16 ab 99€. Alle Preise inkl. Einbau und 12 Monate Garantie.' },
    { q: 'Verliert mein iPhone Daten beim Akkutausch?', a: 'Nein. Beim Akkutausch wird ausschließlich der Akku gewechselt. Alle Daten, Apps und Einstellungen bleiben vollständig erhalten.' },
    { q: 'Wie lange dauert ein iPhone Akkutausch?', a: 'Der Tausch dauert 30–60 Minuten. Inklusive Versand rechne mit 3–5 Werktagen. Express-Service 24h auf Anfrage.' },
    { q: 'Mein iPhone zeigt nach dem Tausch eine Akkuwarnung – ist das normal?', a: 'Bei iPhones ab 11 zeigt iOS eine Info-Meldung wenn ein Akku eingebaut wurde, der nicht direkt von Apple stammt. Das ist rein informativ und beeinträchtigt die Funktion nicht.' },
    { q: 'Kann ich den iPhone Akku selbst tauschen?', a: 'Theoretisch ja, aber neuere iPhones (ab iPhone 14) haben verklebte Akkus und sind sehr schwer zu öffnen. Ein Fehler kann das Display oder andere Bauteile beschädigen. Professioneller Tausch ist deutlich sicherer.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Alle Modelle · iPhone 16 bis SE
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              iPhone Akku tauschen –<br />
              <span className="text-indigo-400">Alle Modelle & Preise</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              iPhone hält nicht mehr lange oder Akku aufgebläht? Wir tauschen Akkus für alle iPhone Modelle – professionell, mit 12 Monaten Garantie, bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Modell wählen & Preis prüfen
              </button>
              <a href="mailto:service@phone-medic.de" className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Per E-Mail anfragen
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 49 €', label: 'Akkutausch' },
              { number: '30–60 min', label: 'Reparaturzeit' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Wann sollte der Akku getauscht werden?</h2>
            <p className="text-gray-500">Diese Symptome zeigen, dass dein iPhone einen neuen Akku braucht</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Preise nach Modell</h2>
            <p className="text-gray-500">Alle Preise inklusive Einbau und 12 Monate Garantie</p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 bg-gray-800 text-white px-6 py-3 text-sm font-semibold">
              <span>Modell</span>
              <span className="text-right">Preis</span>
            </div>
            {prices.map((p, i) => (
              <div key={i} className={`grid grid-cols-2 px-6 py-4 text-sm border-b border-gray-200 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <span className="text-gray-700 font-medium">{p.model}</span>
                <span className="text-right font-bold text-indigo-600">{p.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">Exakte Preise für dein Modell direkt im Reparatur-Konfigurator.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere iPhone Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'iPhone Display Reparatur', path: '/iphone-display-reparatur' },
              { label: 'iPhone startet nicht', path: '/iphone-startet-nicht' },
              { label: 'iPhone Neustart-Schleife', path: '/iphone-neustart-schleife' },
              { label: 'Alle iPhone Reparaturen', path: '/repair' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">iPhone Akku tauschen lassen</h2>
          <p className="text-gray-400 mb-8">Alle Modelle · ab 49 € · 12 Monate Garantie · Bundesweiter Einsende-Service</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Modell auswählen & buchen
            </button>
            <a href="mailto:service@phone-medic.de" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Per E-Mail anfragen
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default IPhoneAkkuTauschenPage
